import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const MenuItem = ({ item, onNavigate }) => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [isExpanded, setIsExpanded] = useState(false);
  const isRTL = i18n.language === 'ar';

  // Check if current path matches this item or any of its submenu items
  const isActive = () => {
    if (item.path && location.pathname === item.path) {
      return true;
    }
    if (item.submenu) {
      return item.submenu.some(subItem => location.pathname === subItem.path);
    }
    return false;
  };

  // Check if a submenu item is active
  const isSubItemActive = (subItemPath) => {
    return location.pathname === subItemPath;
  };

  const handleClick = () => {
    if (item.submenu) {
      // Toggle submenu expansion
      setIsExpanded(!isExpanded);
    } else if (item.path) {
      // Navigate to the path
      navigate(item.path);
      onNavigate && onNavigate();
    }
  };

  const handleSubItemClick = (subItemPath) => {
    navigate(subItemPath);
    onNavigate && onNavigate();
  };

  // RTL-aware classes
  const iconSpacingClass = isRTL ? 'space-x-reverse space-x-3' : 'space-x-3';
  const chevronMarginClass = isRTL ? 'mr-2' : 'ml-2';
  const submenuPaddingClass = isRTL ? 'pr-8' : 'pl-8';
  const subItemPaddingClass = isRTL ? 'px-6' : 'px-6';

  return (
    <div className="mb-1">
      {/* Main menu item */}
      <button
        onClick={handleClick}
        className={`w-full flex items-center justify-between px-4 py-3 text-left hover:bg-[#106363] transition-colors duration-200 rounded-none
          ${isActive() ? `bg-[#106363]` : ''}
        `}
        dir={isRTL ? 'rtl' : 'ltr'}
      >
        <div className={`flex items-center ${iconSpacingClass}`}>
          {item.icon}
          <span className="font-medium">{item.title}</span>
        </div>
        
        {/* Show chevron for items with submenu */}
        {item.submenu && (
          <div className={chevronMarginClass}>
            {isExpanded ? (
              <ChevronUp className="w-4 h-4" />
            ) : (
              <ChevronDown className="w-4 h-4" />
            )}
          </div>
        )}
      </button>

      {/* Submenu items */}
      {item.submenu && isExpanded && (
        <div className={`bg-[#0C4747] ${submenuPaddingClass}`} dir={isRTL ? 'rtl' : 'ltr'}>
          {item.submenu.map((subItem, subIndex) => (
            <button
              key={subIndex}
              onClick={() => handleSubItemClick(subItem.path)}
              className={`w-full text-left ${subItemPaddingClass} py-2 transition-colors duration-200 flex items-center
                ${isSubItemActive(subItem.path) ? `text-white font-medium` : 'text-gray-200'}
              `}
              dir={isRTL ? 'rtl' : 'ltr'}
            >
              {subItem.title}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default MenuItem;